
import React, { useState, useEffect, useRef } from 'react';
import { InteractiveService, InteractiveSessionState } from '../../services/interactive/InteractiveService';
import { QRCodeSVG } from 'qrcode.react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import * as mammoth from 'mammoth'; // Import mammoth
import {
    Users,
    Maximize,
    X,
    Upload,
    BarChart2,
    Type,
    MessageSquare,
    Play,
    StopCircle,
    Copy,
    Check,
    Settings,
    Layout,
    Cloud,
    Volume2,
    VolumeX,
    FileText
} from 'lucide-react';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// --- Types ---
type QuestionType = 'word_cloud' | 'mcq' | 'short_answer';

interface Question {
    id: string | number;
    type: QuestionType;
    text: string;
    options?: string[]; // For MCQ    
    correctAnswer?: string; // Optional for auto-grading later
    timeLimit?: number;
    image?: string;
}

// --- Components ---

// Helper to render text with LaTeX
// Helper to render text with LaTeX
const renderLatexText = (text: string) => {
    if (!text) return "";

    // Split by $$...$$ for block math, then $...$ for inline
    // Regex: /(\$\$[^\$]+\$\$|\$[^\$]+\$)/g
    const parts = text.split(/(\$\$[\s\S]+?\$\$|\$[^\$]+?\$)/g);

    return parts.map((part, index) => {
        if (part.startsWith('$$') && part.endsWith('$$') && part.length > 4) {
            const math = part.slice(2, -2);
            return <BlockMath key={index} math={math} />;
        }
        if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
            const math = part.slice(1, -1);
            return <InlineMath key={index} math={math} />;
        }
        return <span key={index}>{part}</span>;
    });
};

const QRModal = ({ code, isOpen, onClose }: { code: string, isOpen: boolean, onClose: () => void }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={onClose}>
            <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-lg w-full text-center relative animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors">
                    <X className="w-6 h-6 text-gray-500" />
                </button>

                <h2 className="text-3xl font-bold text-gray-800 mb-2">Tham gia lớp học</h2>
                <div className="text-xl text-gray-500 mb-8">Quét mã QR hoặc nhập Code</div>

                <div className="flex justify-center mb-8">
                    <div className="p-4 bg-white border-4 border-indigo-100 rounded-2xl shadow-inner">
                        <QRCodeSVG value={`https://ebook.vatli365.vn/live/${code}`} size={256} />
                    </div>
                </div>

                <div className="flex items-center justify-center gap-4 mb-8">
                    <div className="text-6xl font-black text-indigo-600 tracking-widest font-mono bg-indigo-50 px-8 py-4 rounded-xl border-2 border-indigo-100">
                        {code}
                    </div>
                </div>

                <div className="text-lg text-gray-600">
                    Truy cập: <span className="font-bold text-indigo-600">ebook.vatli365.vn/live</span>
                </div>
            </div>
        </div>
    );
};

interface TeacherSessionProps {
    /** Câu hỏi nạp sẵn (vd: câu hỏi tương tác THẬT của bài giảng khi mở từ thẻ Phiên trực tiếp). */
    initialQuestions?: Question[];
}

const TeacherSession: React.FC<TeacherSessionProps> = ({ initialQuestions }) => {
    // Session State
    const [sessionCode, setSessionCode] = useState<string | null>(null);
    const [sessionState, setSessionState] = useState<InteractiveSessionState | null>(null);
    const [ws, setWs] = useState<WebSocket | null>(null);
    const [activeTab, setActiveTab] = useState<'create' | 'control'>('create');
    const [showQR, setShowQR] = useState(false);

    // Question Management State — khởi tạo bằng câu hỏi thật của bài giảng nếu có
    const [questions, setQuestions] = useState<Question[]>(initialQuestions || []);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1);
    const [previewQ, setPreviewQ] = useState<Question | null>(null); // câu đang XEM TRƯỚC trên máy GV (chưa phát cho HS)

    // Manual Creation State (for "Quick Ask")
    const [quickType, setQuickType] = useState<QuestionType>('mcq');
    const [quickText, setQuickText] = useState('');
    const [quickOptions, setQuickOptions] = useState<string[]>(['A', 'B', 'C', 'D']);
    const [quickCorrect, setQuickCorrect] = useState<string[]>([]);     // đáp án đúng cho câu nhanh (MCQ, có thể nhiều)
    const [activeCorrect, setActiveCorrect] = useState<string[]>([]);   // đáp án đúng của câu đang phát (host xem)
    const [locked, setLocked] = useState(false);                        // đã khoá câu hỏi
    const [timeLeft, setTimeLeft] = useState<number | null>(null);      // đếm ngược
    const [scores, setScores] = useState<Record<string, number>>({});   // điểm tích luỹ theo tên
    const timerRef = useRef<any>(null);
    const scoredQ = useRef<string | null>(null);
    const QUESTION_SECONDS = 30;

    // Sound State
    const [isMuted, setIsMuted] = useState(false);
    const audioRefs = useRef<{
        lobby: HTMLAudioElement;
        active: HTMLAudioElement;
        response: HTMLAudioElement;
        victory: HTMLAudioElement;
    } | null>(null);

    // Initialize Audio
    useEffect(() => {
        audioRefs.current = {
            lobby: new Audio('/sounds/game-intro.mp3'),
            active: new Audio('/sounds/game-background.mp3'),
            response: new Audio('/sounds/mixkit-correct-answer-fast-notification-953.wav'),
            victory: new Audio('/sounds/game-victory.mp3')
        };

        // Configure loops
        audioRefs.current.lobby.loop = true;
        audioRefs.current.active.loop = true;

        // Set volumes
        audioRefs.current.lobby.volume = 0.3;
        audioRefs.current.active.volume = 0.2; // Softer background
        audioRefs.current.response.volume = 0.6; // Distinct
        audioRefs.current.victory.volume = 0.5;

        return () => {
            // Cleanup: stop all sounds
            if (audioRefs.current) {
                Object.values(audioRefs.current).forEach(audio => {
                    audio.pause();
                    audio.currentTime = 0;
                });
            }
        };
    }, []);

    // Manage BGM based on state
    useEffect(() => {
        if (!audioRefs.current || isMuted) {
            // If muted, pause everything
            audioRefs.current?.lobby.pause();
            audioRefs.current?.active.pause();
            return;
        }

        const { lobby, active } = audioRefs.current;

        if (activeTab === 'create') {
            // Prepare screen (no sound or maybe lobby sound?)
            // Let's keep silent here or play soft intro? 
            // Better silent until "Start Session" to not annoy user immediately.
            lobby.pause();
            active.pause();
        } else if (activeTab === 'control') {
            if (sessionState?.current_question) {
                // Question is active -> Play Active BGM, Stop Lobby
                lobby.pause();
                active.play().catch(() => { }); // Catch autoplay block
            } else {
                // Lobby/Waiting -> Play Lobby BGM, Stop Active
                active.pause();
                lobby.play().catch(() => { });
            }
        }
    }, [activeTab, sessionState?.current_question, isMuted]);

    // Play Response Sound
    useEffect(() => {
        if (!audioRefs.current || isMuted) return;

        // Check if response count increased
        if (sessionState?.responses && sessionState.responses.length > 0) {
            // Simple way: Play sound on every update if length changes
            // We need a ref to track previous length to only play on NEW response
            // But here we don't have prev state easily accessbile without another ref.
            // Let's valid this logic: sessionState updates when new response comes.
            // We can just play sound.
            // But initial load might trigger it. Use a timestamp check?
            // Or simpler: just play it, it's fine for "game feel".
            // Actually, re-render might cause spam if not careful.
            // Best to use a ref for prev count.
        }
    }, [sessionState?.responses, isMuted]);

    // Ref for previous response count to trigger sound correctly
    const prevResponseCount = useRef(0);
    useEffect(() => {
        const count = sessionState?.responses?.length || 0;
        if (count > prevResponseCount.current) {
            if (!isMuted && audioRefs.current) {
                // Clone helps play multiple sounds overlapping fast
                const sound = audioRefs.current.response.cloneNode() as HTMLAudioElement;
                sound.volume = 0.6;
                sound.play().catch(() => { });
            }
        }
        prevResponseCount.current = count;
    }, [sessionState?.responses?.length, isMuted]);

    // UI State
    const [isSidebarOpen, setSidebarOpen] = useState(true);

    const startSession = async () => {
        try {
            const code = await InteractiveService.createSession();
            setSessionCode(code);
            const socket = InteractiveService.connectHost(code, (state) => {
                setSessionState(state);
            });
            setWs(socket);
            setActiveTab('control');
            setShowQR(true); // Auto show QR on start
        } catch (err) {
            alert('Không thể tạo phiên: ' + err);
        }
    };

    // ...

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.name.endsWith('.json')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    // ... (keep existing JSON logic)
                    const json = JSON.parse(event.target?.result as string);
                    if (Array.isArray(json)) {
                        const validQuestions = json.filter(q => q.text && q.type);
                        setQuestions(validQuestions);
                        alert(`Đã nhập thành công ${validQuestions.length} câu hỏi từ JSON!`);
                    }
                } catch (err) {
                    alert('Lỗi đọc file JSON: ' + err);
                }
            };
            reader.readAsText(file);
        } else if (file.name.endsWith('.docx')) {
            // Ưu tiên parser server (công thức MathType/OMML→LaTeX, tách ảnh). Lỗi → fallback mammoth.
            try {
                const { questions: qs } = await InteractiveService.parseDocx(file);
                if (Array.isArray(qs) && qs.length > 0) {
                    const mapped: Question[] = qs.map((q: any, i: number) => ({
                        id: 'docx-' + i,
                        type: (q.type as QuestionType) || (q.options?.length >= 2 ? 'mcq' : 'short_answer'),
                        text: q.text || '',
                        options: q.options || undefined,
                        correctAnswer: Array.isArray(q.correctAnswers) && q.correctAnswers.length ? q.correctAnswers.join(', ') : undefined,
                        image: q.image || undefined,
                    }));
                    setQuestions(mapped);
                    alert(`Đã nhập ${mapped.length} câu hỏi (có công thức & hình ảnh nếu có).`);
                    e.target.value = '';
                    return;
                }
                alert('Không tìm thấy câu hỏi trong file. Kiểm tra định dạng "Câu N", phương án "A.", dòng "Đáp án: ...".');
            } catch (err) {
                console.warn('parse-docx server lỗi, thử mammoth:', err);
                const reader = new FileReader();
                reader.onload = async (event) => {
                    try {
                        const arrayBuffer = event.target?.result as ArrayBuffer;
                        const result = await (mammoth as any).convertToHtml({ arrayBuffer }, { styleMap: ['u => u'] });
                        const { questions: parsedQuestions } = parseDocxHtml(result.value);
                        if (parsedQuestions.length > 0) { setQuestions(parsedQuestions); alert(`Đã nhập ${parsedQuestions.length} câu hỏi (chế độ dự phòng, có thể thiếu công thức/ảnh).`); }
                        else alert('Không đọc được câu hỏi từ file.');
                    } catch (e2) { alert('Lỗi đọc file Word: ' + e2); }
                };
                reader.readAsArrayBuffer(file);
            }
        }
    };

    // Helper to parse HTML from Docx
    const parseDocxHtml = (html: string): { questions: Question[], errors: string[] } => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const questions: Question[] = [];
        const errors: string[] = [];

        let currentQ: Partial<Question> | null = null;
        let currentTypeExplicit = false; // If type was set by [MC] code

        const paragraphs = Array.from(doc.body.children);

        // Regex patterns
        const explicitTypeRegex = /^\[(MC|WC|SA)\]/i;
        const genericStartRegex = /^(Câu|Bài)\s*\d+[:.]?/i;
        const optionRegex = /^([A-D])\s*[\.:)]/i; // A. or A) or A:
        const answerKeyRegex = /^(Đáp án|Đáp số|Answer|ĐS|Đ\.án)[:\s]+(.*)/i;

        const codeToType: Record<string, QuestionType> = {
            'MC': 'mcq',
            'WC': 'word_cloud',
            'SA': 'short_answer'
        };

        const pushCurrentQuestion = () => {
            if (!currentQ) return;

            // Finalize Type Inference
            if (!currentQ.type) {
                // If it has options, it's MCQ
                if (currentQ.options && currentQ.options.length >= 2) {
                    currentQ.type = 'mcq';
                } else {
                    // Default to Short Answer if simple text
                    currentQ.type = 'short_answer';
                }
            }

            // Validation
            if (currentQ.text) {
                if (currentQ.type === 'mcq') {
                    if (!currentQ.options || currentQ.options.length < 2) {
                        errors.push(`Câu hỏi "${currentQ.text.substring(0, 20)}..." (MCQ) thiếu đáp án lựa chọn.`);
                    }
                    if (!currentQ.correctAnswer) {
                        errors.push(`Câu hỏi "${currentQ.text.substring(0, 20)}..." chưa có đáp án đúng.`);
                    }
                } else {
                    // SA / WC
                    if (!currentQ.correctAnswer) {
                        // Warning only for now
                        // errors.push(`Câu hỏi SA/WC "${currentQ.text.substring(0, 20)}..." thiếu đáp án.`);
                    }
                }
                questions.push(currentQ as Question);
            }
        };

        paragraphs.forEach((p, idx) => {
            let text = p.textContent?.trim() || '';
            const htmlContent = p.innerHTML;

            // 1. Check for New Question Start
            const explicitMatch = text.match(explicitTypeRegex);
            const genericMatch = text.match(genericStartRegex);

            if (explicitMatch || genericMatch) {
                // Finish previous
                pushCurrentQuestion();

                // Init New
                currentQ = {
                    id: Date.now() + Math.random(),
                    text: '',
                    options: [],
                    correctAnswer: ''
                };

                if (explicitMatch) {
                    currentQ.type = codeToType[explicitMatch[1].toUpperCase()];
                    currentTypeExplicit = true;
                    text = text.substring(explicitMatch[0].length).trim();
                } else {
                    currentQ.type = undefined; // Infer later
                    currentTypeExplicit = false;
                    // Remove "Câu 1:" prefix to be clean
                    if (genericMatch) {
                        text = text.substring(genericMatch[0].length).trim();
                    }
                }

                currentQ.text = text;

                const img = p.querySelector('img');
                if (img) currentQ.image = img.src;

                return;
            }

            // 2. Process Content if inside a question
            if (currentQ) {
                // Check Answer Key
                const answerMatch = text.match(answerKeyRegex);
                if (answerMatch) {
                    currentQ.correctAnswer = answerMatch[2].trim();
                    return;
                }

                // Check Options (A. B. C. D.) - mostly MCQ
                if (!currentTypeExplicit || currentQ.type === 'mcq') {
                    const optionMatch = text.match(optionRegex);
                    if (optionMatch) {
                        currentQ.type = 'mcq';
                        const label = optionMatch[1].toUpperCase();
                        const content = text.substring(optionMatch[0].length).trim();

                        currentQ.options = currentQ.options || [];
                        currentQ.options.push(content);

                        // Check Underline
                        const uTag = p.querySelector('u');
                        if (uTag) {
                            currentQ.correctAnswer = content;
                        }
                        return;
                    }
                }

                // Normal Text / Image appending
                const img = p.querySelector('img');
                if (img) {
                    if (!currentQ.image) currentQ.image = img.src;
                } else if (text) {
                    if (currentQ.text) currentQ.text += '\n' + text;
                    else currentQ.text = text;
                }
            } else {
                // Orphan text
                if (text && text.length > 20 && !text.startsWith('Hướng dẫn') && !text.startsWith('Danh sách')) {
                    errors.push(`Dòng ${idx}: Nội dung chưa thuộc câu hỏi nào: "${text.substring(0, 30)}..."`);
                }
            }
        });

        // Push last
        pushCurrentQuestion();

        return { questions, errors };
    };



    const lockNow = () => {
        if (ws) ws.send(JSON.stringify({ action: 'LOCK' }));
        setLocked(true);
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        setTimeLeft(null);
    };
    const startTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setTimeLeft(QUESTION_SECONDS);
        timerRef.current = setInterval(() => {
            setTimeLeft((t) => {
                if (t === null) return null;
                if (t <= 1) { lockNow(); return 0; }
                return t - 1;
            });
        }, 1000);
    };
    // Chấm điểm khi khoá: MCQ đúng = tập đáp án trùng activeCorrect → +1
    useEffect(() => {
        if (!locked) return;
        const cq = sessionState?.current_question as any;
        if (!cq || cq.type !== 'mcq' || activeCorrect.length === 0) return;
        if (scoredQ.current === cq.timestamp) return;
        scoredQ.current = cq.timestamp;
        const correctKey = [...activeCorrect].map(String).sort().join('|');
        setScores((prev) => {
            const next = { ...prev };
            (sessionState?.responses || []).forEach((r) => {
                const ans = Array.isArray(r.answer) ? r.answer : [r.answer];
                if (ans.map(String).sort().join('|') === correctKey) next[r.user] = (next[r.user] || 0) + 1;
            });
            return next;
        });
    }, [locked, sessionState, activeCorrect]);
    useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

    const playQuestion = (q: Question) => {
        if (!ws || !sessionCode) return;
        // host ghi nhớ (các) đáp án đúng (KHÔNG gửi cho HS); hỗ trợ nhiều đáp án "A, C"
        setActiveCorrect(q.type === 'mcq' && q.correctAnswer ? q.correctAnswer.split(',').map(s => s.trim()).filter(Boolean) : []);

        const payload = {
            type: q.type,
            data: {
                text: q.text,
                options: q.type === 'mcq' ? q.options : undefined,
                image: q.image || undefined,
            }
        };

        ws.send(JSON.stringify({
            action: 'SET_QUESTION',
            payload: payload
        }));
        setLocked(false);
        scoredQ.current = null;
        setPreviewQ(null); // đã phát → rời chế độ xem trước, chuyển sang kết quả trực tiếp
        startTimer();
    };

    const playQuickQuestion = () => {
        const q: Question = {
            id: 'quick-' + Date.now(),
            type: quickType,
            text: quickText || 'Câu hỏi nhanh',
            options: quickType === 'mcq' ? quickOptions : undefined,
            correctAnswer: quickType === 'mcq' && quickCorrect.length ? quickCorrect.join(', ') : undefined,
        };
        playQuestion(q);
    };

    // --- Renderers ---

    const renderChart = () => {
        if (!sessionState?.responses) return <div className="text-gray-400 italic">Chưa có dữ liệu...</div>;

        const currentQ = sessionState.current_question;
        if (!currentQ) return null;

        if (currentQ.type === 'mcq') {
            const options: string[] = currentQ.data.options || ['A', 'B', 'C', 'D'];
            const counts: Record<string, number> = {};
            options.forEach((opt: string) => counts[opt] = 0);

            sessionState.responses.forEach(r => {
                const a = r.answer;
                if (Array.isArray(a)) a.forEach((x) => { if (counts[x] !== undefined) counts[x]++; });
                else if (counts[a] !== undefined) counts[a]++;
            });

            const labels = Object.keys(counts);
            const totalResp = sessionState.responses.length;
            const totalP = sessionState.participants_count || 0;
            const palette = ['rgba(59,130,246,0.85)', 'rgba(245,158,11,0.85)', 'rgba(139,92,246,0.85)', 'rgba(236,72,153,0.85)', 'rgba(14,165,233,0.85)'];
            const bg = labels.map((opt, i) => activeCorrect.includes(opt) ? 'rgba(16,185,129,0.92)' : palette[i % palette.length]);

            // Thống kê đúng/sai (so tập đáp án HS chọn với activeCorrect)
            const correctKey = [...activeCorrect].map(String).sort().join('|');
            const correctCount = activeCorrect.length
                ? sessionState.responses.filter(r => {
                    const ans = Array.isArray(r.answer) ? r.answer : [r.answer];
                    return ans.map(String).sort().join('|') === correctKey;
                }).length
                : 0;
            const accuracy = totalResp ? Math.round((correctCount / totalResp) * 100) : 0;
            const notAnswered = Math.max(0, totalP - totalResp);

            const data = {
                labels,
                datasets: [{ label: 'Số lượng', data: labels.map(o => counts[o]), backgroundColor: bg, borderRadius: 8, borderSkipped: false as const }],
            };

            const StatCard = ({ label, value, accent }: { label: string; value: any; accent?: string }) => (
                <div className={`rounded-xl p-3 text-center border ${accent === 'emerald' ? 'bg-emerald-50 border-emerald-200' : accent === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className={`text-2xl font-black ${accent === 'emerald' ? 'text-emerald-600' : accent === 'amber' ? 'text-amber-600' : 'text-indigo-600'}`}>{value}</div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 font-bold mt-0.5">{label}</div>
                </div>
            );

            return (
                <div className="w-full">
                    <div className="flex items-center justify-between mb-2 text-sm">
                        <span className="text-gray-500">Đã trả lời: <b className="text-indigo-600">{totalResp}{totalP ? ` / ${totalP}` : ''}</b></span>
                        {activeCorrect.length > 0 && <span className="text-emerald-600 font-bold">✓ Đáp án đúng: {activeCorrect.join(', ')}</span>}
                    </div>
                    <div className="h-[340px] w-full">
                        <Bar
                            data={data}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: { display: false },
                                    tooltip: { backgroundColor: 'rgba(0,0,0,0.8)', padding: 12, titleFont: { size: 14 }, bodyFont: { size: 14 } }
                                },
                                scales: { y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: '#f3f4f6' } }, x: { grid: { display: false } } }
                            }}
                        />
                    </div>

                    {/* ── THỐNG KÊ CHI TIẾT ── */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                        <StatCard label="Đã vào lớp" value={totalP} />
                        <StatCard label="Đã trả lời" value={totalResp} accent="amber" />
                        {activeCorrect.length > 0 && <StatCard label="Trả lời đúng" value={correctCount} accent="emerald" />}
                        {activeCorrect.length > 0 && <StatCard label="Tỉ lệ đúng" value={`${accuracy}%`} accent="emerald" />}
                        {activeCorrect.length === 0 && <StatCard label="Chưa trả lời" value={notAnswered} />}
                        {activeCorrect.length === 0 && <StatCard label="Số phương án" value={labels.length} />}
                    </div>

                    {/* Phần trăm theo từng phương án */}
                    <div className="mt-4 space-y-2">
                        {labels.map((opt, i) => {
                            const c = counts[opt];
                            const pct = totalResp ? Math.round((c / totalResp) * 100) : 0;
                            const isCorrect = activeCorrect.includes(opt);
                            return (
                                <div key={opt} className="flex items-center gap-3 text-sm">
                                    <span className={`w-7 h-7 shrink-0 flex items-center justify-center rounded-lg font-bold ${isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>{String.fromCharCode(65 + i)}</span>
                                    <div className="flex-1 bg-slate-100 rounded-full h-3.5 overflow-hidden">
                                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: bg[i] }} />
                                    </div>
                                    <span className="w-24 text-right text-gray-500 font-medium">{c} · {pct}%{isCorrect && ' ✓'}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        } else if (currentQ.type === 'word_cloud') {
            const words = sessionState.responses.map(r => r.answer);
            const freqs: Record<string, number> = {};
            words.forEach(w => {
                // Case insensitive count
                const lower = w.toLowerCase();
                freqs[lower] = (freqs[lower] || 0) + 1;
            });

            // Sort by freq descending
            const sortedWords = Object.entries(freqs).sort((a, b) => b[1] - a[1]);

            // Color palette from user image
            const cloudColors = [
                '#0ea5e9', // Sky Blue
                '#10b981', // Emerald Green (Exercise)
                '#f59e0b', // Amber (Orange)
                '#8b5cf6', // Violet
                '#ec4899', // Pink
                '#ef4444', // Red
                '#6366f1', // Indigo
            ];

            return (
                <div className="flex flex-wrap gap-x-8 gap-y-4 items-center justify-center p-12 min-h-[400px] content-center">
                    {sortedWords.map(([word, freq], idx) => {
                        // Calculate size: Base 2rem + (freq * 1.5)rem, capped at 6rem
                        const size = Math.min(2 + (freq * 1.5), 6);
                        const color = cloudColors[idx % cloudColors.length];
                        // Random rotation between -5 and 5 degrees for organic look
                        const rotation = Math.random() * 10 - 5;

                        return (
                            <span
                                key={word}
                                className="font-black leading-none transition-all duration-700 ease-out animate-in zoom-in cursor-default hover:scale-110"
                                style={{
                                    fontSize: `${size}rem`,
                                    color: color,
                                    transform: `rotate(${rotation}deg)`,
                                    textShadow: '2px 2px 0px rgba(0,0,0,0.05)'
                                }}
                                title={`${freq} người chọn`}
                            >
                                {word.charAt(0).toUpperCase() + word.slice(1)}
                            </span>
                        );
                    })}
                    {sortedWords.length === 0 && (
                        <div className="text-gray-300 text-2xl font-light italic">Running word cloud...</div>
                    )}
                </div>
            );
        } else {
            // Short Answer - Hide Names
            const totalP = sessionState.participants_count || 0;
            return (
              <div className="w-full">
                <div className="text-sm text-gray-500 mb-3 text-center">Đã trả lời: <b className="text-indigo-600">{sessionState.responses.length}{totalP ? ` / ${totalP}` : ''}</b></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-max max-h-[500px] overflow-y-auto p-4 content-start">
                    {sessionState.responses.map((r, i) => (
                        <div key={i} className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-l-indigo-400 hover:shadow-md transition-all animate-in slide-in-from-bottom-2">
                            <p className="text-gray-800 text-xl font-medium break-words leading-relaxed font-sans">
                                {r.answer}
                            </p>
                        </div>
                    ))}
                    {sessionState.responses.length === 0 && (
                        <div className="col-span-full text-center py-12 text-gray-400 text-lg italic">
                            Chờ câu trả lời từ học sinh...
                        </div>
                    )}
                </div>
              </div>
            )
        }
    };

    // --- Main UI ---
    if (activeTab === 'create') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center p-4">
                <div className="bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl max-w-md w-full text-center border border-white/20">
                    <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl shadow-inner">
                        🎓
                    </div>
                    <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                        Interactive Class
                    </h1>
                    <p className="text-gray-500 mb-8 text-lg">Tạo lớp học tương tác thời gian thực</p>

                    <button
                        onClick={startSession}
                        className="w-full bg-indigo-600 text-white py-4 rounded-2xl text-xl font-bold shadow-lg hover:bg-indigo-700 hover:shadow-indigo-500/30 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 group"
                    >
                        <Play className="w-6 h-6 fill-current" />
                        Bắt đầu ngay
                    </button>

                    <div className="mt-8 pt-8 border-t border-gray-100">
                        <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-2">Được hỗ trợ bởi</p>
                        <div className="flex justify-center items-center gap-2 font-bold text-gray-600">
                            <span className="text-indigo-600">Vật Lí 365</span> Ebook Ecosystem
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans text-slate-800">
            {/* Header Toolbar */}
            <header className="bg-white border-b px-4 py-3 flex justify-between items-center shadow-sm z-20 relative">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setSidebarOpen(!isSidebarOpen)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                    >
                        <Layout className="w-6 h-6" />
                    </button>
                    <div className="flex flex-col">
                        <h1 className="text-lg font-bold text-gray-800 leading-none">Interactive Session</h1>
                        <span className="text-xs text-gray-500">Live Control Center</span>
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    {/* Sound Toggle */}
                    <button
                        onClick={() => setIsMuted(!isMuted)}
                        className={`p-2 rounded-full transition-all ${isMuted ? 'bg-gray-100 text-gray-400' : 'bg-indigo-100 text-indigo-600 shadow-sm'}`}
                        title={isMuted ? "Bật âm thanh" : "Tắt âm thanh"}
                    >
                        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>

                    {/* Access Info Pill */}
                    <div
                        className="flex items-center bg-indigo-50 hover:bg-indigo-100 transition-colors rounded-full pl-1 pr-6 py-1 cursor-pointer border border-indigo-100"
                        onClick={() => setShowQR(true)}
                    >
                        <div className="bg-white p-2 rounded-full shadow-sm mr-3">
                            <QRCodeSVG value={`https://ebook.vatli365.vn/live/${sessionCode}`} size={24} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-[10px] uppercase font-bold text-indigo-400 tracking-wider">Join Code</span>
                            <span className="text-xl font-black text-indigo-700 leading-none tracking-widest">{sessionCode}</span>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-xl">
                        <Users className="w-5 h-5 text-gray-500" />
                        <span className="font-bold text-gray-700">{sessionState?.participants_count || 0}</span>
                    </div>

                    <button className="bg-red-50 text-red-600 px-4 py-2 rounded-lg font-medium hover:bg-red-100 transition-colors text-sm">
                        Kết thúc
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Sidebar */}
                <div className={`${isSidebarOpen ? 'w-80' : 'w-0'} bg-white border-r transition-all duration-300 flex flex-col overflow-hidden`}>
                    <div className="p-4 border-b">
                        <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Công cụ
                        </h3>

                        {/* File Upload */}
                        <div className="mb-6">
                            <label className="block w-full cursor-pointer bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-4 text-center hover:bg-slate-100 hover:border-indigo-400 transition-all group">
                                <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400 group-hover:text-indigo-500" />
                                <span className="text-sm font-medium text-slate-600 group-hover:text-indigo-600 block">Tải file câu hỏi (.json, .docx)</span>
                                <input type="file" accept=".json,.docx" onChange={handleFileUpload} className="hidden" />
                            </label>
                            <p className="text-xs text-gray-400 mt-2 text-center leading-relaxed">
                                Câu MCQ: đáp án đúng <b>gạch chân</b>. Tag: <b>[DS]</b> Đúng/Sai, <b>[CLOUD]</b> Word&nbsp;Cloud, <b>[TEXT]</b> Trả&nbsp;lời&nbsp;ngắn.
                            </p>
                            <a
                                href="/mau-phien-truc-tiep.docx"
                                download
                                className="mt-2 flex items-center justify-center gap-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-700 hover:underline"
                            >
                                <FileText className="w-3.5 h-3.5" /> Tải file mẫu (.docx)
                            </a>
                        </div>

                        {/* Quick Question Tabs */}
                        <div>
                            <div className="flex bg-slate-100 p-1 rounded-lg mb-3">
                                {[
                                    { id: 'mcq', icon: BarChart2, label: 'MCQ' },
                                    { id: 'word_cloud', icon: Cloud, label: 'Cloud' },
                                    { id: 'short_answer', icon: Type, label: 'Text' }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setQuickType(tab.id as QuestionType)}
                                        className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-md text-xs font-medium transition-all ${quickType === tab.id ? 'bg-white shadow text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                                    >
                                        <tab.icon className="w-4 h-4" />
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <textarea
                                value={quickText}
                                onChange={e => setQuickText(e.target.value)}
                                placeholder="Nhập câu hỏi nhanh..."
                                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 mb-3"
                                rows={3}
                            />

                            {quickType === 'mcq' && (
                                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                                    {quickOptions.map((opt, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="w-6 h-8 flex items-center justify-center bg-slate-200 text-xs font-bold rounded text-slate-600">
                                                {String.fromCharCode(65 + i)}
                                            </span>
                                            <input
                                                value={opt}
                                                onChange={e => {
                                                    const newOpts = [...quickOptions];
                                                    const old = newOpts[i];
                                                    newOpts[i] = e.target.value;
                                                    setQuickOptions(newOpts);
                                                    setQuickCorrect((c) => c.map(x => x === old ? e.target.value : x));
                                                }}
                                                className="flex-1 p-1 bg-white border border-slate-200 rounded text-sm px-2"
                                            />
                                            <label className="flex items-center gap-1 text-[11px] text-emerald-600 font-semibold shrink-0" title="Đánh dấu đáp án đúng (có thể chọn nhiều)">
                                                <input type="checkbox" checked={quickCorrect.includes(opt) && opt !== ''} onChange={() => setQuickCorrect((c) => c.includes(opt) ? c.filter(x => x !== opt) : [...c, opt])} /> đúng
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button onClick={playQuickQuestion} className="w-full btn-primary py-2.5 rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition-all">
                                Phát ngay ⚡
                            </button>
                        </div>
                    </div>

                    {/* Question List from File */}
                    <div className="flex-1 overflow-y-auto p-4">
                        <h4 className="font-bold text-gray-500 text-xs uppercase tracking-wider mb-3">Danh sách câu hỏi ({questions.length})</h4>
                        <div className="space-y-2">
                            {questions.map((q, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => {
                                        // Chọn để XEM TRƯỚC (biên dịch công thức/ảnh) trên máy GV — chưa phát cho HS
                                        setCurrentQuestionIndex(idx);
                                        setPreviewQ(q);
                                    }}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:bg-indigo-50 ${currentQuestionIndex === idx ? 'bg-indigo-50 border-indigo-500 shadow-sm' : 'bg-white border-transparent hover:border-indigo-200'}`}
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${q.type === 'mcq' ? 'bg-blue-100 text-blue-600' : q.type === 'word_cloud' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                                            {q.type.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-800 line-clamp-2 font-medium">{renderLatexText(q.text)}</p>
                                </div>
                            ))}
                            {questions.length === 0 && (
                                <div className="text-center py-8 text-gray-400 text-xs">
                                    Chưa có câu hỏi nào.
                                    <br />Hãy tải file hoặc tạo nhanh.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Main View */}
                <div className="flex-1 bg-slate-100 p-8 flex flex-col items-center justify-center relative overflow-y-auto">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#cbd5e1 1.5px, transparent 1.5px)', backgroundSize: '24px 24px' }}></div>

                    {previewQ ? (
                        /* ── XEM TRƯỚC trên máy GV (đã biên dịch công thức/ảnh) — chưa phát cho HS ── */
                        <div className="w-full max-w-4xl z-10 animate-in fade-in duration-300">
                            <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-indigo-100">
                                <div className="bg-amber-500 px-6 py-3 flex items-center justify-between text-white">
                                    <span className="font-bold flex items-center gap-2">👁 Xem trước — chưa phát cho học sinh</span>
                                    <span className="text-xs uppercase font-bold bg-white/20 px-3 py-1 rounded">{previewQ.type.replace('_', ' ')}</span>
                                </div>
                                <div className="p-8">
                                    <div className="text-2xl font-bold text-gray-800 mb-4 leading-relaxed">{renderLatexText(previewQ.text)}</div>
                                    {previewQ.image && (
                                        <img src={previewQ.image} alt="minh hoạ" className="max-h-72 object-contain rounded-lg border mb-4 bg-white p-2" />
                                    )}
                                    {previewQ.type === 'mcq' && previewQ.options && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {previewQ.options.map((opt, i) => {
                                                const correctSet = (previewQ.correctAnswer || '').split(',').map(s => s.trim()).filter(Boolean);
                                                const isCorrect = correctSet.includes(opt);
                                                return (
                                                    <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${isCorrect ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
                                                        <span className={`w-7 h-7 shrink-0 flex items-center justify-center rounded-full font-bold text-sm ${isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>{String.fromCharCode(65 + i)}</span>
                                                        <div className="flex-1 text-gray-800">{renderLatexText(opt)}{isCorrect && <span className="ml-2 text-emerald-600 text-sm font-bold">✓ đúng</span>}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {previewQ.type !== 'mcq' && (
                                        <div className="text-gray-400 italic">Học sinh sẽ nhập câu trả lời {previewQ.type === 'word_cloud' ? '(word cloud)' : 'ngắn'}.</div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center justify-center gap-3 mt-6">
                                <button onClick={() => playQuestion(previewQ)} className="bg-indigo-600 text-white px-8 py-3 rounded-xl shadow-lg font-bold hover:bg-indigo-700 flex items-center gap-2 text-lg">
                                    <Play className="w-5 h-5 fill-current" /> Phát cho học sinh
                                </button>
                                <button onClick={() => setPreviewQ(null)} className="bg-white text-gray-600 px-5 py-3 rounded-xl border font-medium hover:bg-gray-50">
                                    {sessionState?.current_question ? 'Quay lại kết quả' : 'Đóng'}
                                </button>
                            </div>
                        </div>
                    ) : sessionState?.current_question ? (
                        <div className="w-full max-w-5xl z-10 animate-in fade-in duration-500">
                            {/* Question Card */}
                            <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-8 border border-white/50">
                                <div className="bg-indigo-600 px-8 py-6 flex items-start gap-4 text-white">
                                    <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-sm mt-1">
                                        {sessionState.current_question.type === 'mcq' && <BarChart2 className="w-8 h-8" />}
                                        {sessionState.current_question.type === 'word_cloud' && <Cloud className="w-8 h-8" />}
                                        {sessionState.current_question.type === 'short_answer' && <MessageSquare className="w-8 h-8" />}
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-3xl font-bold leading-tight flex flex-col gap-4">
                                            <div>{renderLatexText((sessionState.current_question.data as any).text)}</div>
                                            {(sessionState.current_question.data as any).image && (
                                                <img
                                                    src={(sessionState.current_question.data as any).image}
                                                    alt="Question Illustration"
                                                    className="max-h-64 object-contain rounded-lg bg-white p-2 w-auto self-start shadow-md"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-white/10 px-4 py-2 rounded-lg font-mono text-xl font-bold backdrop-blur-sm whitespace-nowrap">
                                        {sessionState.responses.length} Trả lời
                                    </div>
                                </div>

                                {/* Visualization Area */}
                                <div className="p-8 bg-white min-h-[400px] flex items-center justify-center">
                                    {renderChart()}
                                </div>
                            </div>

                            {/* Đếm ngược + Khoá + Bảng xếp hạng */}
                            <div className="flex flex-col items-center gap-4">
                                {!locked ? (
                                    <div className="flex items-center gap-4">
                                        {timeLeft !== null && (
                                            <div className={`px-5 py-3 rounded-xl shadow-lg font-mono text-2xl font-black ${timeLeft <= 5 ? 'bg-red-500 text-white animate-pulse' : 'bg-white text-indigo-600'}`}>⏱ {timeLeft}s</div>
                                        )}
                                        <button onClick={lockNow} className="bg-red-500 text-white px-6 py-3 rounded-xl shadow-lg font-bold hover:bg-red-600 flex items-center gap-2">
                                            <StopCircle className="w-5 h-5" /> Khoá &amp; chốt kết quả
                                        </button>
                                    </div>
                                ) : (
                                    <div className="bg-emerald-50 text-emerald-700 px-6 py-3 rounded-xl font-bold border border-emerald-200">🔒 Đã khoá — kết quả đã chốt</div>
                                )}
                                {Object.keys(scores).length > 0 && (
                                    <div className="bg-white rounded-2xl shadow-lg p-5 w-full max-w-md">
                                        <h3 className="font-black text-gray-700 mb-2 flex items-center gap-2">🏆 Bảng xếp hạng</h3>
                                        <div className="space-y-1">
                                            {Object.entries(scores).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([nm, sc], i) => (
                                                <div key={nm} className="flex items-center justify-between text-sm px-2 py-1.5 rounded bg-slate-50">
                                                    <span className="font-semibold text-gray-700">{['🥇', '🥈', '🥉'][i] || `${i + 1}.`} {nm}</span>
                                                    <span className="text-indigo-600 font-bold">{sc} điểm</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* ── SẢNH CHỜ: hiện số & tên học sinh đã vào lớp ── */
                        <div className="w-full max-w-3xl z-10">
                            <div className="bg-white rounded-3xl shadow-xl p-8 border border-white/50 text-center">
                                <div className="flex items-center justify-center gap-2 mb-5">
                                    <Users className="w-7 h-7 text-indigo-500" />
                                    <h2 className="text-2xl font-black text-gray-700">
                                        Học sinh đã vào lớp: <span className="text-indigo-600">{sessionState?.participants_count || 0}</span>
                                    </h2>
                                </div>
                                {(sessionState?.participants?.length || 0) > 0 ? (
                                    <div className="flex flex-wrap gap-2 justify-center max-h-[320px] overflow-y-auto">
                                        {sessionState!.participants.map((name, i) => (
                                            <span key={i} className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full font-semibold text-sm border border-indigo-100 animate-in zoom-in duration-300">
                                                {name}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-400 italic py-6">
                                        Chưa có học sinh nào. Yêu cầu HS quét QR hoặc nhập mã{' '}
                                        <b className="text-indigo-600">{sessionCode}</b> tại ebook.vatli365.vn/live
                                    </p>
                                )}
                                <div className="mt-6 pt-6 border-t text-gray-500 text-sm">
                                    Chọn câu hỏi bên trái để <b>xem trước</b>, rồi bấm <b>Phát cho học sinh</b>.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <QRModal
                code={sessionCode || ''}
                isOpen={showQR}
                onClose={() => setShowQR(false)}
            />
        </div>
    );
};

export default TeacherSession;
